import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useTheme, useMediaQuery } from '@material-ui/core';
import { platform_wnstnToken } from 'store/platform/selectors';
import { system_theme } from 'store/system/selectors';
import { useStyles } from './wnstn-widget.styles';
import { WNSTN_WIDGET_SCRIPT_URL, WNSTN_CONTAINER_CLASS, WNSTN_INNER_CONTAINER_CLASS } from '@leverate/common-client';
import { WNSTN_FULL_PROXY_URL } from 'features/wnstn-widget/wnstn-widget.constants';
import { mapLanguageToWnstn } from '@leverate/common-client';
import { mapThemeToWnstn } from './wnstn-widget.utils';
import type { WnstnChatInitParams } from './wnstn-widget.types';
import { useWnstnContext } from './wnstn-widget.context';
import { useBodyStyleRestore } from 'hooks/useBodyStyleRestore';

const WnstnPanel: React.FC = () => {
    const muiTheme = useTheme();
    const isXs = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const { isOpen, wnstnUserParams, anchorRect, bottomNavHeight } = useWnstnContext();

    const theme = useSelector(system_theme);
    const wnstnToken = useSelector(platform_wnstnToken);
    const classes = useStyles();
    const {
        i18n: { language },
    } = useTranslation();

    const isXsRef = useRef(isXs);
    useEffect(() => {
        isXsRef.current = isXs;
    }, [isXs]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const scriptElRef = useRef<HTMLScriptElement | null>(null);
    const { saveBodyStyles, restoreBodyStyles } = useBodyStyleRestore();

    useEffect(() => {
        if (!wnstnUserParams || !isOpen || !wnstnToken) {
            return;
        }

        if (isXsRef.current) saveBodyStyles();

        const chatInitParams: WnstnChatInitParams = {
            userId: wnstnUserParams.userId,
            proxy: WNSTN_FULL_PROXY_URL,
            color_theme: mapThemeToWnstn(theme),
            language_for_chat: mapLanguageToWnstn(language),
            user_class: wnstnUserParams.userClass || undefined,
            client_token: wnstnToken,
        };

        window.chatInitParams = chatInitParams;

        const outerContainer = document.querySelector<HTMLElement>(`.${WNSTN_CONTAINER_CLASS}`);
        const innerContainer = containerRef.current;
        if (outerContainer) {
            outerContainer.style.display = '';
        }
        if (innerContainer) {
            innerContainer.style.display = '';
        }

        if (!innerContainer) {
            return;
        }

        const script = document.createElement('script');
        script.src = WNSTN_WIDGET_SCRIPT_URL;
        script.async = true;
        innerContainer.appendChild(script);
        scriptElRef.current = script;

        return () => {
            if (scriptElRef.current && scriptElRef.current.parentNode) {
                scriptElRef.current.parentNode.removeChild(scriptElRef.current);
            }
            scriptElRef.current = null;
            if (innerContainer) {
                innerContainer.innerHTML = '';
            }
            delete window.chatInitParams;
            restoreBodyStyles();
        };
    }, [wnstnUserParams, isOpen, theme, language, wnstnToken]);

    const anchorStyle: React.CSSProperties | undefined =
        anchorRect && !isXs
            ? { top: anchorRect.top, maxHeight: `calc(100vh - ${anchorRect.top}px - 20px)` }
            : undefined;

    const wrapperStyle: React.CSSProperties = {
        ...anchorStyle,
        ...(isXs ? { bottom: bottomNavHeight } : undefined),
    };

    return (
        <div
            className={classNames(classes.wrapper, {
                [classes.wrapperOpen]: isOpen,
                [classes.wrapperClosed]: !isOpen,
            })}
            style={wrapperStyle}
        >
            <div className={`${WNSTN_CONTAINER_CLASS} ${classes.container}`}>
                <div ref={containerRef} className={WNSTN_INNER_CONTAINER_CLASS} />
            </div>
        </div>
    );
};

export { WnstnPanel };
export { WnstnTriggerButton } from './WnstnTriggerButton';
export { WnstnWrapper } from './WnstnWrapper';
export { WnstnProvider } from './wnstn-widget.context';
export default WnstnPanel;
